(function (global) {
  "use strict";

  const fonts = ["Instrument Serif", "Inter", "Fraunces", "Playfair Display", "Caveat", "Nunito"];
  const clamp = (n, low, high) => Math.min(high, Math.max(low, n));

  function fontString(state, size, family) {
    return `${state.italic ? "italic " : ""}${state.bold ? 700 : 400} ${size}px "${family || state.font}", Georgia, serif`;
  }

  function wrapped(ctx, value, width) {
    const lines = [];
    for (const paragraph of String(value).split(/\r?\n/)) {
      let line = "";
      for (const word of paragraph.trim().split(/\s+/).filter(Boolean)) {
        if (ctx.measureText(word).width > width) {
          if (line) lines.push(line);
          line = "";
          for (const char of word) {
            if (line && ctx.measureText(line + char).width > width) { lines.push(line); line = ""; }
            line += char;
          }
        } else if (line && ctx.measureText(`${line} ${word}`).width > width) {
          lines.push(line);
          line = word;
        } else line = line ? `${line} ${word}` : word;
      }
      lines.push(line);
    }
    return lines;
  }

  function measure(ctx, value, width, size, options) {
    ctx.font = fontString(options, size, options.font);
    const lines = wrapped(ctx, value, width);
    let ascent = 0, descent = 0;
    for (const line of lines) {
      const m = ctx.measureText(line || "Mg");
      ascent = Math.max(ascent, m.actualBoundingBoxAscent || size * .8);
      descent = Math.max(descent, m.actualBoundingBoxDescent || size * .2);
    }
    const leading = Math.max(size * options.lineHeight, ascent + descent + size * .06);
    return { lines, size, ascent, descent, leading, h: (lines.length - 1) * leading + ascent + descent };
  }

  function fit(ctx, value, width, height, size, options) {
    let layout = measure(ctx, value, width, size, options);
    while (layout.h > height && size > 6) { size -= 1; layout = measure(ctx, value, width, size, options); }
    return layout;
  }

  function drawText(ctx, layout, box, options) {
    ctx.fillStyle = options.ink;
    ctx.font = fontString(options, layout.size, options.font);
    ctx.textAlign = options.align;
    ctx.textBaseline = "alphabetic";
    const x = box.x + (options.align === "center" ? box.w / 2 : options.align === "right" ? box.w : 0);
    layout.lines.forEach((line, i) => ctx.fillText(line, x, box.y + layout.ascent + i * layout.leading));
  }

  function imageCrop(image, box, state) {
    const iw = image.naturalWidth, ih = image.naturalHeight;
    const scale = Math.max(box.w / iw, box.h / ih) * state.photoZoom;
    const w = box.w / scale, h = box.h / scale;
    return { x: (iw - w) * state.photoX / 100, y: (ih - h) * state.photoY / 100, w, h };
  }

  function drawPhoto(ctx, image, box, state) {
    const source = imageCrop(image, box, state);
    ctx.save();
    ctx.beginPath(); ctx.rect(box.x, box.y, box.w, box.h); ctx.clip();
    ctx.filter = state.photoEffect === "mono" ? "grayscale(1)" : state.photoEffect === "warm" ? "sepia(.4) saturate(.85)" : "none";
    ctx.drawImage(image, source.x, source.y, source.w, source.h, box.x, box.y, box.w, box.h);
    ctx.restore();
    return source;
  }

  function render(ctx, state, width, height, image = null, qr = null) {
    const product = global.FTCRenderer.productFor(state.product);
    const [fullW, fullH] = global.FTCRenderer.exportSize(state.product);
    const scale = width / 1000, h = height / scale;
    const unit = 1000 / fullW, trimW = product.trim[0] * unit;
    const typeScale = trimW / 1000;
    const bleed = product.bleed * unit;
    const trim = { x: bleed, y: bleed, w: trimW, h: h - bleed * 2 };
    const padding = Math.max(product.safe * unit, trim.w * state.margin / 100);
    const box = { x: trim.x + padding, y: trim.y + padding, w: trim.w - 2 * padding, h: trim.h - 2 * padding };
    const textOptions = { ...state, font: fonts.includes(state.font) ? state.font : fonts[0] };
    const smallOptions = { ...textOptions, font: "Inter", bold: false, italic: false, uppercase: false, lineHeight: 1.4 };
    const gap = 27 * typeScale;
    const blocks = [];
    let photoBox = null, photoCrop = null;
    ctx.save();
    ctx.setTransform(1, 0, 0, 1, 0, 0); ctx.clearRect(0, 0, width, height); ctx.scale(scale, scale);
    ctx.fillStyle = state.background; ctx.fillRect(0, 0, 1000, h);

    if (image && state.photoLayout === "background") {
      photoBox = { x: 0, y: 0, w: 1000, h };
      photoCrop = drawPhoto(ctx, image, photoBox, state);
      ctx.fillStyle = `rgba(0,0,0,${state.photoOverlay / 100})`; ctx.fillRect(0, 0, 1000, h);
    }

    if (state.border !== "none") {
      const inset = Math.max(product.safe * unit, padding * .55);
      ctx.strokeStyle = state.ink; ctx.lineWidth = 2 * typeScale;
      ctx.strokeRect(trim.x + inset, trim.y + inset, trim.w - inset * 2, trim.h - inset * 2);
      if (state.border === "double") ctx.strokeRect(trim.x + inset + 10 * typeScale, trim.y + inset + 10 * typeScale, trim.w - inset * 2 - 20 * typeScale, trim.h - inset * 2 - 20 * typeScale);
    }

    const addText = (value, size, options, maxH) => {
      if (!String(value).trim()) return;
      const layout = fit(ctx, value, box.w, maxH, size * typeScale, options);
      blocks.push({ type: "text", layout, options, h: layout.h });
    };
    addText(state.title, 24, { ...smallOptions, bold: true }, box.h * .12);
    const addPhoto = () => {
      if (image && state.photoLayout !== "background") blocks.push({ type: "photo", h: box.h * state.photoHeight / 100 });
    };
    if (state.photoLayout === "above") addPhoto();
    let words = state.uppercase ? state.text.toLocaleUpperCase("en") : state.text;
    if (state.quoteMarks && words.trim()) words = `\u201c${words}\u201d`;
    const main = words.trim() ? { type: "main", options: textOptions, h: 0 } : null;
    if (main) blocks.push(main);
    if (state.photoLayout === "below") addPhoto();
    const hasSignature = [state.author, state.detail, state.date, state.note].some(value => value.trim());
    if (state.divider && hasSignature) blocks.push({ type: "divider", h: 3 * typeScale });
    addText(state.author, 29, smallOptions, box.h * .1);
    addText(state.detail, 21, smallOptions, box.h * .09);
    addText(state.date, 21, smallOptions, box.h * .09);
    addText(state.note, 23, smallOptions, box.h * .19);
    if (state.showQr && qr) blocks.push({ type: "qr", h: 105 * typeScale });

    const othersH = blocks.reduce((sum, block) => sum + block.h, 0) + Math.max(0, blocks.length - 1) * gap;
    if (main) {
      main.layout = fit(ctx, words, box.w, Math.max(12, box.h - othersH), state.fontSize * typeScale, textOptions);
      main.h = main.layout.h;
    }
    const total = blocks.reduce((sum, block) => sum + block.h, 0) + Math.max(0, blocks.length - 1) * gap;
    let y = box.y + (state.position === "top" ? 0 : state.position === "bottom" ? box.h - total : (box.h - total) / 2);
    const blockBounds = [];
    for (const block of blocks) {
      const bounds = { x: box.x, y, w: box.w, h: block.h, type: block.type };
      blockBounds.push(bounds);
      if (block.type === "text" || block.type === "main") drawText(ctx, block.layout, bounds, block.options);
      else if (block.type === "photo") { photoBox = bounds; photoCrop = drawPhoto(ctx, image, bounds, state); }
      else if (block.type === "divider") {
        const lineW = box.w * .16;
        const x = box.x + (state.align === "left" ? 0 : state.align === "right" ? box.w - lineW : (box.w - lineW) / 2);
        ctx.fillStyle = state.ink; ctx.fillRect(x, y, lineW, 2 * typeScale);
      } else if (block.type === "qr") {
        const size = block.h;
        const x = box.x + (state.align === "left" ? 0 : state.align === "right" ? box.w - size : (box.w - size) / 2);
        ctx.fillStyle = "#ffffff"; ctx.fillRect(x, y, size, size);
        const cell = size / (qr.count + 8);
        ctx.fillStyle = "#111111";
        for (let row = 0; row < qr.count; row++) for (let col = 0; col < qr.count; col++) {
          if (qr.cells[row * qr.count + col]) ctx.fillRect(x + (col + 4) * cell, y + (row + 4) * cell, cell + .01, cell + .01);
        }
      }
      y += block.h + gap;
    }
    ctx.restore();
    return { width: 1000, height: h, box, trim, blockBounds, photoBox, photoCrop,
      textSize: main ? main.layout.size / typeScale : state.fontSize,
      fitAdjusted: !!main && main.layout.size < state.fontSize * typeScale - .5,
      fits: total <= box.h + 1, fullW, fullH };
  }

  function watermark(ctx, width, height) {
    ctx.save(); ctx.translate(width / 2, height / 2); ctx.rotate(-Math.PI / 7);
    ctx.font = `700 ${Math.round(width * .04)}px Inter, sans-serif`; ctx.textAlign = "center"; ctx.textBaseline = "middle";
    ctx.lineWidth = 2; ctx.strokeStyle = "rgba(255,255,255,.4)"; ctx.fillStyle = "rgba(28,32,38,.28)";
    const label = "framethiscomment.com \u00b7 preview", span = Math.hypot(width, height);
    const stepX = ctx.measureText(label).width + width * .1, stepY = width * .22;
    for (let y = -span; y < span; y += stepY) for (let x = -span; x < span; x += stepX) {
      const offset = Math.round(y / stepY) % 2 ? stepX / 2 : 0;
      ctx.strokeText(label, x + offset, y); ctx.fillText(label, x + offset, y);
    }
    ctx.restore();
  }

  global.FTCStudioRenderer = { renderArtwork: render, fonts, watermark, clamp };
})(window);
